import { createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./i18n";
import "./index.css";
import { App } from "./index.tsx";
import { routeTree } from "./routeTree.gen.ts";
import { auth } from "./lib/auth.ts";
import { getPersonalInfoQueryOptions } from "./hooks/workers/index.ts";
import { usePreloadData } from "./hooks/pre-load/get.hooks.ts";
import { Loading } from "./components/loading-animation.tsx";
const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function PreloadData() {
  const { isLoading, progress } = usePreloadData();
  
  if (isLoading) {
    return <Loading progress={progress} />;
  }
  
  return null;
}

function mount(el: JSX.Element) {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>{el}</StrictMode>,
  );
}

async function main() {
  if (location.pathname === "/login-complete") {
    await auth.onLogin().then(() => {
      window.location.href = "/reservations/calendar";
    });
    return;
  }

  if (location.pathname === "/silent-refresh") {
    await auth.onSilentRefresh();
    return mount(<></>);
  }

  const authenticated = await userIsAuthenticated();

  if (!authenticated) {
    return;
  }

  mount(
    <App router={router}>
      <PreloadData />
    </App>
  );
}

main();

async function userIsAuthenticated(): Promise<boolean> {
  let authenticated = false;
  try {
    authenticated = await auth.loggedIn;
    if (!authenticated) return false;
    if (!getPersonalInfoQueryOptions.queryFn) {
      return false;
    }
    await getPersonalInfoQueryOptions.queryFn({} as never);
    return true;
  } catch (e) {
    console.error(e);
    if (authenticated) {
      await auth.logout();
    } else {
      await auth.login();
    }
    return false;
  }
}
